import React from "react";
import { IonModal, IonIcon, IonButton } from "@ionic/react";
import { close } from "ionicons/icons";
import { useBLE } from "../../../hooks/useBle";

interface ActionButton {
  text: string;
  action: () => void;
  isPrimary?: boolean;
  color?: string;
  backgroundColor?: string;
}

interface RevokeAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  actionButtons: ActionButton[];
  backgroundColor?: string;
  buttonColor?: string;
  isDarkMode?: boolean;
  showDeviceInfo?: boolean;
  deviceInfo?: {
    serialNumber?: string;
    deviceId?: string;
    deviceType?: string;
  };
  showPulseDevice?: boolean;
}

interface DeviceActionItemProps {
  menuItem: string;
  label: string;
  showToggle?: boolean;
  toggleEnabled?: boolean;
  onToggle?: () => void;
}

const DeviceActionItem: React.FC<DeviceActionItemProps> = ({
  menuItem,
  label,
  showToggle = false,
  toggleEnabled = false,
  onToggle,
}) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        padding: "11px 16px",
        gap: "8px",
        width: "309px",
        height: "64px",
        background: "#1C1C1E",
        borderRadius: "20px",
        flex: "none",
        order: 1,
        flexGrow: 0,
      }}
    >
      {/* Menu Item + Label Container */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: "0px",
          width: "277px",
          height: "42px",
          flex: "none",
          order: 0,
          flexGrow: 1,
        }}
      >
        {/* Menu Item */}
        <div
          style={{
            width: "277px",
            height: "22px",
            fontFamily:
              "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "18px",
            lineHeight: "22px",
            textAlign: "center",
            fontFeatureSettings: '"case" on',
            color: "#FFFFFF",
            flex: "none",
            order: 0,
            alignSelf: "stretch",
            flexGrow: 0,
          }}
        >
          {menuItem}
        </div>

        {/* Label */}
        <div
          style={{
            width: "277px",
            height: "20px",
            fontFamily:
              "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "20px",
            textAlign: "center",
            fontFeatureSettings: '"case" on',
            color: "#B0B4B7",
            flex: "none",
            order: 1,
            alignSelf: "stretch",
            flexGrow: 0,
          }}
        >
          {label}
        </div>
      </div>

      {/* Toggle */}
      {showToggle && (
        <div
          onClick={onToggle}
          style={{
            display: "flex",
            width: "51px",
            height: "31px",
            background: toggleEnabled ? "#30D158" : "#8E8E93",
            borderRadius: "100px",
            flex: "none",
            order: 1,
            flexGrow: 0,
            cursor: "pointer",
            alignItems: "center",
            justifyContent: toggleEnabled ? "flex-end" : "flex-start",
            padding: "2px",
            transition: "all 0.3s ease",
          }}
        >
          <div
            style={{
              width: "27px",
              height: "27px",
              background: "#FFFFFF",
              borderRadius: "50%",
              transition: "all 0.3s ease",
            }}
          />
        </div>
      )}
    </div>
  );
};

const ActionButtonsConnected: React.FC<RevokeAccessModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  actionButtons,
  isDarkMode,
  showDeviceInfo = false,
  deviceInfo = null,
  showPulseDevice = false,
}) => {
  const textColor = isDarkMode ? "#FFFFFF" : "#000000";
  const bgColor = isDarkMode ? "#222222" : "#000000";
  const { pulseOximeterDevice } = useBLE();

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      className={`-modal ${
        isDarkMode ? "device-connection-modal" : "container-modal"
      }`}
      style={{
        "--color": textColor,
        "--background": bgColor,
        "--border-radius": "30px",
        "--width": "362px",
        "--height": "402px",
      }}
    >
      <div className="p-6 flex flex-col items-center overflow-y-auto h-full">
        {/* Header with close button */}
        <div className="w-full flex justify-end mb-4">
          <button onClick={onClose} className="text-gray-400">
            <IonIcon
              icon={close}
              style={{ fontSize: "24px", color: textColor }}
            />
          </button>
        </div>

        {/* Modal Title */}
        <h2 className="text-2xl font-bold mb-2" style={{ color: textColor }}>
          {title}
        </h2>

        {/* Device Info - positioned right after title, before message */}
        {showDeviceInfo && (
          <div className="w-full flex justify-center mb-6">
            <DeviceActionItem
              menuItem={
                showPulseDevice
                  ? pulseOximeterDevice?.device.name?.split("_")[1] || "Unknown Device"
                  : deviceInfo?.serialNumber || "Unknown Device"
              }
              label={
                showPulseDevice
                  ? pulseOximeterDevice?.device.name?.split("_")[0] || "Unknown Device"
                  : deviceInfo?.deviceType || "Unknown Device"
              }
              showToggle={false}
              toggleEnabled={false}
            />
          </div>
        )}

        {/* Confirmation Message */}
        <h3
          className="text-xl font-medium mb-8 text-center"
          style={{ color: textColor }}
        >
          {message}
        </h3>

        {/* Action Buttons */}
        <div className="w-full space-y-4 pt-4">
          {actionButtons.map((button, index) => (
            <IonButton
              key={index}
              expand="block"
              onClick={button.action}
              fill={button.isPrimary ? "solid" : "outline"}
              className={
                button.text === "Disconnect"
                  ? "danger-button w-full"
                  : button.isPrimary
                  ? "primary-button w-full"
                  : "tertiary-button w-full"
              }
              style={{
                "--border-radius": "30px",
                marginBottom: index < actionButtons.length - 1 ? "16px" : "0",
                fontSize: "18px",
                fontWeight: "500",
                height: "52px",
                ...(button.isPrimary
                  ? {}
                  : { "--color": textColor, borderRadius: "30px" }),
              }}
            >
              {button.text}
            </IonButton>
          ))}
        </div>
      </div>
    </IonModal>
  );
};

export default ActionButtonsConnected;
